/**
 * Plan 20a, Task 2.2 — HP bar chip damage hook
 *
 * Factory: createHpBarChipHook(config: UiVfxHookConfig)
 *   Returns a UiVfxHook<HTMLElement, { newHpPercent: number; oldHpPercent: number; isHeal?: boolean }>
 *   with id 'hp-bar-chip'.
 *
 * Target element structure:
 *   <div>  ← container (target)
 *     <div data-hp-ghost>  ← trailing ghost bar, behind the fill
 *     <div data-hp-fill>   ← instant fill bar
 *   </div>
 *
 * DAMAGE path (isHeal === false, newHpPercent < oldHpPercent):
 *   1. Instantly set [data-hp-fill] style.width = newHpPercent% (synchronous, no tween)
 *   2. Gsap tweens [data-hp-ghost] width from oldHpPercent% → newHpPercent% over
 *      animation.duration.slow (500ms, fallback from data-vfx-duration-slow) with snappy ease
 *   3. Brief red shadow flash on target for 200ms via data-hp-flash="damage" attribute
 *      (consumer CSS handles the visual; attribute is removed after 200ms)
 *
 * HEAL path (isHeal === true, newHpPercent > oldHpPercent):
 *   1. Instantly set BOTH [data-hp-fill] and [data-hp-ghost] style.width = newHpPercent%
 *   2. Brief green flash (#44FF44 at 30% opacity) for 200ms via data-hp-flash="heal" attribute
 *
 * LOW state (newHpPercent < 30):
 *   Sets data-hp-low='true' on the container. Consumer CSS handles the 60–100% opacity
 *   2s pulse (cheaper than a gsap repeat loop for idle animations).
 *   Removed when newHpPercent >= 30.
 *
 * dispose():
 *   Kills in-flight timelines and removes data-hp-low from the last-played target.
 *
 * NOTE — completion callbacks:
 *   The gsap-registry sentinel tween extends every timeline to VIRTUAL_DURATION=3600s.
 *   eventCallback('onComplete') on the root timeline fires at 3600s, not at the natural
 *   animation end. Use timeline.call(fn, undefined, absoluteSeconds) instead.
 *   Canonical reference: src/client/renderer/animation/hit-reaction.ts:17-18.
 */

import type { UiVfxHook, UiVfxHookConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public opts type
// ─────────────────────────────────────────────────────────────────────────────

export interface HpBarChipOpts {
  newHpPercent: number;
  oldHpPercent: number;
  isHeal?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers — token reading (same pattern as button-states.ts)
// ─────────────────────────────────────────────────────────────────────────────

/** Reads a millisecond duration from a data-vfx-* attribute; fallback in ms. */
function readDurationMs(el: HTMLElement, attr: string, fallbackMs: number): number {
  const raw = el.dataset[attr];
  if (raw) {
    const parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return fallbackMs;
}

/** Reads an easing string from a data-vfx-* attribute; falls back to provided default. */
function readEasing(el: HTMLElement, attr: string, fallback: string): string {
  const raw = el.dataset[attr];
  return raw && raw.trim().length > 0 ? raw.trim() : fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// Flash helper — sets a data-hp-flash attribute for 200ms then removes it
// Implemented without gsap tweening: uses a single timeline.call at 0.2s
// so the flash lifetime stays within the timeline and is cancelled on dispose.
// ─────────────────────────────────────────────────────────────────────────────
const FLASH_DURATION_S = 0.2;

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createHpBarChipHook(
  config: UiVfxHookConfig,
): UiVfxHook<HTMLElement, HpBarChipOpts> {
  const { gsapRegistry } = config;

  /** In-flight handle; at most one active at a time. */
  let currentHandle: GsapTimelineHandle | null = null;
  /** Last target that had data-hp-low set; tracked for dispose() cleanup. */
  let lastLowTarget: HTMLElement | null = null;

  /** Kill the current in-flight timeline, if any. */
  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  function play(target: HTMLElement, opts?: HpBarChipOpts): GsapTimelineHandle {
    killCurrent();

    const newHp = opts?.newHpPercent ?? 0;
    const oldHp = opts?.oldHpPercent ?? 0;
    const isHeal = opts?.isHeal ?? false;

    // ── Child element lookup ─────────────────────────────────────────────────
    const fillEl = target.querySelector<HTMLElement>('[data-hp-fill]');
    const ghostEl = target.querySelector<HTMLElement>('[data-hp-ghost]');

    // ── Read timing tokens from element data-attributes (with fallbacks) ─────
    // data-vfx-duration-slow → dataset.vfxDurationSlow
    const slowMs = readDurationMs(target, 'vfxDurationSlow', 500);
    const snappyEase = readEasing(target, 'vfxEasingSnappy', 'power2.out');
    const slowSec = slowMs / 1000;

    const handle = gsapRegistry.create('hp-bar-chip');
    const tl = handle.timeline;

    if (!isHeal) {
      // ── DAMAGE path ─────────────────────────────────────────────────────────

      // 1. Instantly set [data-hp-fill] width (SYNCHRONOUS — no tween)
      if (fillEl) {
        fillEl.style.width = `${newHp}%`;
      }

      // 2. Tween [data-hp-ghost] from oldHp% → newHp% over slow (500ms)
      if (ghostEl) {
        // Set ghost starting width to oldHp% immediately (so tween starts from correct value)
        ghostEl.style.width = `${oldHp}%`;
        tl.to(
          ghostEl,
          {
            width: `${newHp}%`,
            duration: slowSec,
            ease: snappyEase,
          },
          0,
        );
      }

      // 3. Brief red flash via data-hp-flash="damage" attribute for 200ms
      target.setAttribute('data-hp-flash', 'damage');
      tl.call(
        () => {
          target.removeAttribute('data-hp-flash');
        },
        undefined,
        FLASH_DURATION_S,
      );
    } else {
      // ── HEAL path ───────────────────────────────────────────────────────────

      // 1. Instantly set BOTH bars to newHp%
      if (fillEl) {
        fillEl.style.width = `${newHp}%`;
      }
      if (ghostEl) {
        ghostEl.style.width = `${newHp}%`;
      }

      // 2. Brief green flash via data-hp-flash="heal" attribute for 200ms
      target.setAttribute('data-hp-flash', 'heal');
      tl.call(
        () => {
          target.removeAttribute('data-hp-flash');
        },
        undefined,
        FLASH_DURATION_S,
      );
    }

    // ── LOW state ─────────────────────────────────────────────────────────────
    // Set data-hp-low='true' when newHp < 30; remove when newHp >= 30.
    // This is a plain attribute toggle — CSS handles the opacity pulse.
    if (newHp < 30) {
      target.setAttribute('data-hp-low', 'true');
      lastLowTarget = target;
    } else {
      target.removeAttribute('data-hp-low');
      if (lastLowTarget === target) {
        lastLowTarget = null;
      }
    }

    currentHandle = handle;
    return handle;
  }

  function dispose(): void {
    // Remove data-hp-low from the last-played target that had it set
    if (lastLowTarget) {
      lastLowTarget.removeAttribute('data-hp-low');
      lastLowTarget = null;
    }
    killCurrent();
  }

  return {
    id: 'hp-bar-chip',
    play,
    dispose,
  };
}
